import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import AnalyticsPanel from "@/components/AnalyticsPanel";
import AdminBanners from "@/components/AdminBanners";
import AdminContactMessages from "@/components/AdminContactMessages";
import ImageUploadField from "@/components/ImageUploadField";
import EditPromptModal from "@/components/EditPromptModal";
import AdminBlogTable from "@/components/AdminBlogTable";
import {
  Upload,
  Loader2,
  Trash2,
  LogOut,
  Plus,
  FileSpreadsheet,
  Download,
  Pencil,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

const PROMPTS_PER_PAGE = 50;

const CATEGORY_OPTIONS = [
  "Corporate",
  "Anime",
  "Vintage",
  "Cyberpunk",
  "Editorial",
  "Minimalist",
  "Street",
  "Fantasy",
  "Cottagecore",
  "Other",
];

const SAMPLE_CSV = `Title,Image_URL,Prompt_Text,Style_Category,Explanation_Text,How_To_Use,Parameters
Sample Title,https://example.com/image.jpg,Your detailed prompt here,Cyberpunk,A 100-200 word explanation of the styling approach that helps users understand why this prompt works.,"1. Open Gemini. 2. Paste. 3. Upload reference photo.","Model: Gemini 2.5, Aspect: 4:5, Style: Cinematic"`;

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total_prompts: 0, total_categories: 0, total_likes: 0 });
  const [prompts, setPrompts] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState("prompts");

  // Manual form state
  const [form, setForm] = useState({
    title: "",
    image_url: "",
    prompt_text: "",
    style_category: "Corporate",
    explanation_text: "",
    how_to_use: "",
    parameters: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // CSV state
  const [csvFile, setCsvFile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoadingList(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/prompts"),
      ]);
      setStats(s.data);
      setPrompts(l.data);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error("Failed to load", { description: msg });
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (ready && user && user.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, user]);

  if (ready && !user) return <Navigate to="/admin-login" replace />;
  if (!ready) return null;

  const submitManual = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/prompts", form);
      toast.success("Prompt added", { description: data.title });
      setForm({
        title: "",
        image_url: "",
        prompt_text: "",
        style_category: form.style_category,
        explanation_text: "",
        how_to_use: "",
        parameters: "",
      });
      load();
    } catch (err) {
      toast.error("Create failed", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Only .csv files are accepted");
      return;
    }
    setCsvFile(file);
    setUploadResult(null);
  };

  const uploadCsv = async () => {
    if (!csvFile) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", csvFile);
      const { data } = await api.post("/admin/prompts/bulk-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(data);
      toast.success(`Inserted ${data.inserted} prompts`, {
        description: data.skipped
          ? `${data.skipped} row(s) skipped`
          : "All rows imported successfully",
      });
      setCsvFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      load();
    } catch (err) {
      toast.error("Upload failed", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setUploading(false);
    }
  };

  const deletePrompt = async (id, title) => {
    if (!window.confirm(`Delete "${title}"?`)) return;
    try {
      await api.delete(`/admin/prompts/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error("Delete failed", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "prompts_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.max(1, Math.ceil(prompts.length / PROMPTS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageOffset = (currentPage - 1) * PROMPTS_PER_PAGE;
  const pagedPrompts = prompts.slice(pageOffset, pageOffset + PROMPTS_PER_PAGE);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono-label text-[#00E5FF]">Admin Workplace</div>
            <h1 className="font-display text-3xl md:text-5xl tracking-tight mt-2">
              Dashboard
            </h1>
            <p className="text-white/50 mt-2 text-sm">
              Signed in as <span className="text-white">{user.email}</span>
            </p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => {
              logout();
              navigate("/");
            }}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 hover:border-white/30 px-4 py-2 text-sm text-white/80"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>

        {/* Stats */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="admin-stats">
          {[
            ["Total prompts", stats.total_prompts],
            ["Categories", stats.total_categories],
            ["Total likes", stats.total_likes],
          ].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-white/10 bg-[#0E0E12] p-6">
              <div className="font-mono-label text-white/50">{l}</div>
              <div className="font-display text-4xl mt-2">{v}</div>
            </div>
          ))}
        </div>

        {/* Site traffic analytics */}
        <AnalyticsPanel />

        {/* Partner banners */}
        <AdminBanners />

        {/* Contact form submissions */}
        <AdminContactMessages />

        {/* Management tabs */}
        <div
          className="mt-10 inline-flex rounded-full border border-white/10 p-1"
          data-testid="admin-management-tabs"
        >
          <button
            type="button"
            onClick={() => setActiveTab("prompts")}
            data-testid="admin-tab-prompts"
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              activeTab === "prompts"
                ? "bg-white text-[#050505]"
                : "text-white/60 hover:text-white"
            }`}
          >
            Prompts Management
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("blog")}
            data-testid="admin-tab-blog"
            className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
              activeTab === "blog"
                ? "bg-white text-[#050505]"
                : "text-white/60 hover:text-white"
            }`}
          >
            Blog Management
          </button>
        </div>

        {activeTab === "blog" && <AdminBlogTable />}

        {activeTab === "prompts" && (
        <>
        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* CSV Upload */}
          <section className="rounded-2xl border border-white/10 bg-[#0E0E12] p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono-label text-[#00E5FF]">Bulk Upload</div>
                <h2 className="font-display text-2xl mt-1">CSV Import</h2>
              </div>
              <button
                type="button"
                data-testid="download-csv-template"
                onClick={downloadTemplate}
                className="inline-flex items-center gap-2 text-xs font-mono-label text-white/60 hover:text-white transition-colors"
              >
                <Download className="h-3 w-3" />
                Template
              </button>
            </div>

            <div
              data-testid="csv-dropzone"
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                if (f) handleFile(f);
              }}
              onClick={() => fileInputRef.current?.click()}
              className={`mt-5 border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                dragOver
                  ? "border-[#E0007A] bg-[#E0007A]/5"
                  : "border-white/15 hover:border-white/30 bg-black/20"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                data-testid="csv-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <FileSpreadsheet className="h-8 w-8 mx-auto text-white/40" />
              <div className="mt-3 font-medium">
                {csvFile ? csvFile.name : "Drop a .csv file or click to browse"}
              </div>
              <div className="mt-1 text-xs text-white/40 font-mono">
                Columns: Title, Image_URL, Prompt_Text, Style_Category, Explanation_Text, How_To_Use, Parameters
              </div>
            </div>

            <button
              type="button"
              disabled={!csvFile || uploading}
              onClick={uploadCsv}
              data-testid="csv-upload-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E0007A] hover:bg-[#ff1493] disabled:opacity-40 text-white font-semibold py-3 glow-magenta transition-transform hover:scale-[1.01]"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Importing…" : "Import CSV"}
            </button>

            {uploadResult && (
              <div
                data-testid="upload-result"
                className="mt-4 rounded-lg border border-white/10 bg-black/30 p-4 text-sm"
              >
                <div>
                  <span className="text-[#00E5FF] font-mono-label">Inserted</span>{" "}
                  <span className="font-mono">{uploadResult.inserted}</span>
                </div>
                <div>
                  <span className="text-white/50 font-mono-label">Skipped</span>{" "}
                  <span className="font-mono">{uploadResult.skipped}</span>
                </div>
                {uploadResult.errors?.length > 0 && (
                  <details className="mt-2 text-white/60">
                    <summary className="cursor-pointer">Errors</summary>
                    <ul className="mt-2 text-xs font-mono space-y-1">
                      {uploadResult.errors.map((e, i) => (
                        <li key={i}>Row {e.row}: {e.reason}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </section>

          {/* Manual entry */}
          <section className="rounded-2xl border border-white/10 bg-[#0E0E12] p-6">
            <div className="font-mono-label text-[#00E5FF]">Manual Entry</div>
            <h2 className="font-display text-2xl mt-1">Add a single prompt</h2>

            <form onSubmit={submitManual} className="mt-5 space-y-4" data-testid="manual-entry-form">
              <div>
                <label className="font-mono-label text-white/60">Title</label>
                <input
                  required
                  data-testid="manual-title"
                  className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div>
                <label className="font-mono-label text-white/60">Image</label>
                <div className="mt-2">
                  <ImageUploadField
                    value={form.image_url}
                    onChange={(url) => setForm({ ...form, image_url: url })}
                  />
                </div>
              </div>
              <div>
                <label className="font-mono-label text-white/60">Style Category</label>
                <input
                  required
                  list="style-category-suggestions"
                  data-testid="manual-category"
                  placeholder="e.g. Cyberpunk, Renaissance, Studio Ghibli…"
                  className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5"
                  value={form.style_category}
                  onChange={(e) => setForm({ ...form, style_category: e.target.value })}
                />
                <datalist id="style-category-suggestions">
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
                <p className="mt-1.5 text-xs text-white/40">
                  Type any category — new ones will appear as filter chips automatically.
                </p>
              </div>
              <div>
                <label className="font-mono-label text-white/60">Prompt Text</label>
                <textarea
                  required
                  rows={4}
                  data-testid="manual-prompt-text"
                  className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 font-mono text-sm"
                  value={form.prompt_text}
                  onChange={(e) => setForm({ ...form, prompt_text: e.target.value })}
                />
              </div>
              <div>
                <label className="font-mono-label text-white/60">
                  Explanation (100–200 words)
                </label>
                <textarea
                  required
                  rows={5}
                  data-testid="manual-explanation"
                  className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm"
                  value={form.explanation_text}
                  onChange={(e) => setForm({ ...form, explanation_text: e.target.value })}
                />
              </div>
              <div>
                <label className="font-mono-label text-white/60">
                  How to Use <span className="text-white/40 normal-case">(optional)</span>
                </label>
                <textarea
                  rows={3}
                  data-testid="manual-how-to-use"
                  placeholder="Short usage guide — e.g., 1. Open Gemini. 2. Paste. 3. Upload your reference photo."
                  className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm"
                  value={form.how_to_use}
                  onChange={(e) => setForm({ ...form, how_to_use: e.target.value })}
                />
              </div>
              <div>
                <label className="font-mono-label text-white/60">
                  Parameters <span className="text-white/40 normal-case">(optional)</span>
                </label>
                <input
                  type="text"
                  data-testid="manual-parameters"
                  placeholder='Model: Gemini 2.5, Aspect: 4:5, Style: Cinematic'
                  className="mt-2 w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#00E5FF] focus:outline-none px-3 py-2.5 text-sm font-mono"
                  value={form.parameters}
                  onChange={(e) => setForm({ ...form, parameters: e.target.value })}
                />
                <p className="mt-1.5 text-xs text-white/40">
                  Comma-separated <span className="font-mono">Key: Value</span> pairs (or JSON). Renders as badges on the card.
                </p>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="manual-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#050505] font-semibold py-3 hover:scale-[1.01] transition-transform disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {submitting ? "Adding…" : "Add prompt"}
              </button>
            </form>
          </section>
        </div>

        {/* Prompt list */}
        <section className="mt-10 rounded-2xl border border-white/10 bg-[#0E0E12] overflow-hidden">
          <div className="p-6 flex items-center justify-between">
            <div>
              <div className="font-mono-label text-[#00E5FF]">Library</div>
              <h2 className="font-display text-2xl mt-1">All prompts</h2>
            </div>
            <span className="font-mono-label text-white/50">
              {prompts.length} entries
            </span>
          </div>

          {loadingList ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-white/40" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-prompt-table">
                <thead className="bg-black/40 text-white/50 font-mono-label">
                  <tr>
                    <th className="text-left px-6 py-3">S.No</th>
                    <th className="text-left px-6 py-3">Preview</th>
                    <th className="text-left px-6 py-3">Title</th>
                    <th className="text-left px-6 py-3">Category</th>
                    <th className="text-left px-6 py-3">Likes</th>
                    <th className="text-right px-6 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedPrompts.map((p, i) => (
                    <tr
                      key={p.id}
                      data-testid={`admin-row-${p.id}`}
                      className="border-t border-white/5 hover:bg-white/[0.02]"
                    >
                      <td
                        className="px-6 py-3 font-mono text-white/50"
                        data-testid={`admin-row-sno-${p.id}`}
                      >
                        {pageOffset + i + 1}
                      </td>
                      <td className="px-6 py-3">
                        <img
                          src={p.image_url}
                          alt=""
                          loading="lazy"
                          className="h-12 w-12 object-cover rounded-md border border-white/10"
                        />
                      </td>
                      <td className="px-6 py-3 max-w-[320px] truncate">{p.title}</td>
                      <td className="px-6 py-3 text-white/60">{p.style_category}</td>
                      <td className="px-6 py-3 font-mono text-white/60">{p.likes_count}</td>
                      <td className="px-6 py-3 text-right">
                        <div className="inline-flex items-center gap-3">
                          <button
                            onClick={() => setEditing(p)}
                            data-testid={`admin-edit-${p.id}`}
                            className="inline-flex items-center gap-1 text-[#00E5FF] hover:text-white transition-colors text-xs"
                          >
                            <Pencil className="h-3 w-3" />
                            Edit
                          </button>
                          <button
                            onClick={() => deletePrompt(p.id, p.title)}
                            data-testid={`admin-delete-${p.id}`}
                            className="inline-flex items-center gap-1 text-red-300 hover:text-red-100 transition-colors text-xs"
                          >
                            <Trash2 className="h-3 w-3" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {prompts.length === 0 && (
                <div className="text-center py-10 text-white/50">No prompts yet.</div>
              )}
              {prompts.length > 0 && (
                <div
                  className="flex items-center justify-between px-6 py-4 border-t border-white/5"
                  data-testid="admin-prompt-pagination"
                >
                  <span className="text-xs text-white/40 font-mono">
                    Showing {pageOffset + 1}–{Math.min(pageOffset + PROMPTS_PER_PAGE, prompts.length)} of {prompts.length}
                  </span>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      data-testid="admin-prompt-page-prev"
                      className="inline-flex items-center gap-1 rounded-full border border-white/10 disabled:opacity-30 hover:border-white/30 px-3 py-1.5 text-xs text-white/70"
                    >
                      <ChevronLeft className="h-3 w-3" />
                      Prev
                    </button>
                    <span className="font-mono-label text-white/50">
                      Page {currentPage} / {totalPages}
                    </span>
                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      data-testid="admin-prompt-page-next"
                      className="inline-flex items-center gap-1 rounded-full border border-white/10 disabled:opacity-30 hover:border-white/30 px-3 py-1.5 text-xs text-white/70"
                    >
                      Next
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
        </>
        )}
      </div>

      {editing && (
        <EditPromptModal
          prompt={editing}
          onClose={() => setEditing(null)}
          onSaved={() => load()}
        />
      )}
    </div>
  );
}
